(function () {
  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  function sanitizeFilename(name) {
    return String(name || "file").replace(/[^a-zA-Z0-9._-]/g, "_");
  }

  function buildSupabaseHeaders(key, extraHeaders) {
    const headers = {
      apikey: key,
    };

    if (extraHeaders) {
      Object.assign(headers, extraHeaders);
    }

    if (String(key).startsWith("eyJ")) {
      headers.Authorization = `Bearer ${key}`;
    }

    return headers;
  }

  function parseSupabaseError(details) {
    if (!details) return "";

    try {
      const parsed = JSON.parse(details);
      if (parsed.message) return parsed.message;
    } catch {
      // Keep raw response text.
    }

    return details;
  }

  function getField(form, name) {
    const field = form.elements.namedItem(name);
    if (!field) return "";
    if (field.type === "checkbox") return field.checked;
    return String(field.value || "").trim();
  }

  function showError(form, message) {
    let error = form.querySelector(".quote-form__error");

    if (!error) {
      error = document.createElement("p");
      error.className = "quote-form__error";
      error.setAttribute("role", "alert");
      form.querySelector(".quote-form__fields")?.prepend(error);
    }

    error.textContent = message;
    error.hidden = false;
  }

  function clearError(form) {
    const error = form.querySelector(".quote-form__error");
    if (error) error.hidden = true;
  }

  function getFormLabels(form) {
    return {
      sending: form.dataset.labelSending || "Sending...",
      submit: form.dataset.labelSubmit || "Request a quote",
      errorNotConfigured:
        form.dataset.errorNotConfigured ||
        "Quote submission is not configured yet. Please contact support.",
      errorMaxFiles: form.dataset.errorMaxFiles || "You can attach up to 5 files.",
      errorFileSize: form.dataset.errorFileSize || "Each file must be 10 MB or smaller.",
      errorSubmit:
        form.dataset.errorSubmit ||
        "Something went wrong while sending your request. Please try again.",
      attachFilesDefault: form.dataset.attachFilesDefault || "Attach files (up to 5)",
      fileSelectedOne: form.dataset.fileSelectedOne || "1 file selected",
      fileSelectedOther: form.dataset.fileSelectedOther || "{{ count }} files selected",
    };
  }

  function formatFileSelectedLabel(labels, count) {
    if (count === 1) {
      return labels.fileSelectedOne;
    }

    return (labels.fileSelectedOther || "{{ count }} files selected").replace(
      "{{ count }}",
      String(count)
    );
  }

  function setSubmitting(form, isSubmitting) {
    const labels = getFormLabels(form);
    const submitButton = form.querySelector('[type="submit"]');
    const text = submitButton?.querySelector(".btn__text");

    if (submitButton) submitButton.disabled = isSubmitting;
    if (text) {
      text.textContent = isSubmitting ? labels.sending : text.dataset.defaultLabel || labels.submit;
    }
  }

  async function fetchCartItems() {
    try {
      const root = window.Shopify?.routes?.root ?? "/";
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) return [];

      const cart = await response.json();
      if (!Array.isArray(cart.items)) return [];

      return cart.items.map(function (item) {
        return {
          title: item.product_title || item.title,
          variant: item.variant_title,
          quantity: item.quantity,
          sku: item.sku,
          url: item.url,
        };
      });
    } catch {
      return [];
    }
  }

  async function clearCart() {
    const root = window.Shopify?.routes?.root ?? "/";
    const response = await fetch(`${root}cart/clear.js`, {
      method: "POST",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error("Could not clear cart");
    }
  }

  async function uploadAttachment(file, config, requestId) {
    const path = [
      config.shopDomain.replace(/[^a-zA-Z0-9.-]/g, "_"),
      requestId,
      sanitizeFilename(file.name),
    ].join("/");

    const response = await fetch(
      `${config.supabaseUrl}/storage/v1/object/${config.bucket}/${path}`,
      {
        method: "POST",
        headers: buildSupabaseHeaders(config.supabaseAnonKey, {
          "Content-Type": file.type || "application/octet-stream",
          "x-upsert": "false",
        }),
        body: file,
      }
    );

    if (!response.ok) {
      throw new Error(`Attachment upload failed for ${file.name}`);
    }

    return `${config.supabaseUrl}/storage/v1/object/public/${config.bucket}/${path}`;
  }

  function isSupabaseConfigured(config) {
    return Boolean(config.supabaseUrl && config.supabaseAnonKey);
  }

  function buildQuotePayload(form, cartItems, attachments, config) {
    const comment = getField(form, "quote[comment]");
    const cartPayload = cartItems.slice();

    if (comment) {
      cartPayload.push({ type: "comment", value: comment });
    }

    return {
      company_name: getField(form, "quote[company]"),
      first_name: getField(form, "quote[first_name]"),
      last_name: getField(form, "quote[last_name]"),
      company_email: getField(form, "quote[email]"),
      phone_number: getField(form, "quote[phone]"),
      shipping_country: getField(form, "quote[country]"),
      attachments: attachments,
      cart_items: cartPayload,
      source: config.quoteSource === "cart" ? "shopify-quote-cart" : "shopify-quote-form",
    };
  }

  function formatCartLines(cartItems) {
    if (!cartItems.length) {
      return "No products in quote basket.";
    }

    return cartItems
      .map(function (item) {
        const variant =
          item.variant && item.variant !== "Default Title" ? ` (${item.variant})` : "";
        const sku = item.sku ? ` · SKU: ${item.sku}` : "";
        return `- ${item.title}${variant} × ${item.quantity}${sku}`;
      })
      .join("\n");
  }

  function buildContactBody(payload, files, config) {
    const lines = [
      "New quote request",
      "",
      `Company: ${payload.company_name}`,
      `Name: ${payload.first_name} ${payload.last_name}`,
      `Email: ${payload.company_email}`,
      `Phone: ${payload.phone_number}`,
      `Country: ${payload.shipping_country}`,
      `Source: ${payload.source}`,
      `Shop: ${config.shopDomain}`,
    ];

    if (payload.comment) {
      lines.push("", "Comment:", payload.comment);
    } else {
      const commentItem = payload.cart_items.find(function (item) {
        return item && item.type === "comment";
      });
      if (commentItem?.value) {
        lines.push("", "Comment:", commentItem.value);
      }
    }

    lines.push("", "Quote basket:", formatCartLines(payload.cart_items.filter(function (item) {
      return !item || item.type !== "comment";
    })));

    if (files.length) {
      lines.push(
        "",
        "Attachments selected (not uploaded via email fallback):",
        files.map(function (file) { return `- ${file.name}`; }).join("\n")
      );
    }

    return lines.join("\n");
  }

  async function submitViaShopifyContact(form, payload, files, config) {
    const root = window.Shopify?.routes?.root ?? "/";
    const body = new FormData();

    body.append("form_type", "contact");
    body.append("utf8", "✓");
    body.append("contact[name]", `${payload.first_name} ${payload.last_name} (${payload.company_name})`);
    body.append("contact[email]", payload.company_email);
    body.append("contact[body]", buildContactBody(payload, files, config));

    const response = await fetch(`${root}contact`, {
      method: "POST",
      body: body,
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(`Shopify contact submission failed (${response.status})`);
    }
  }

  async function trackQuoteSubmitted(config, cartItems, attachmentCount) {
    if (window.__gosourcelyAmplitude?.track) {
      window.__gosourcelyAmplitude.track("Quote Submitted", {
        source: config.quoteSource,
        item_count: cartItems.length,
        attachment_count: attachmentCount,
        shop_domain: config.shopDomain,
      });

      if (typeof window.__gosourcelyAmplitude.flush === "function") {
        await window.__gosourcelyAmplitude.flush();
      }
    }

    if (typeof window.__gosourcelyGa4TrackQuoteSubmitted === "function") {
      window.__gosourcelyGa4TrackQuoteSubmitted({
        source: config.quoteSource,
        item_count: cartItems.length,
        attachment_count: attachmentCount,
        shop_domain: config.shopDomain,
      });
    }
  }

  async function finalizeQuoteSubmission(form, config, cartItems, attachmentCount) {
    await trackQuoteSubmitted(config, cartItems, attachmentCount);

    try {
      await clearCart();
    } catch (clearCartError) {
      console.warn("Quote saved but cart could not be cleared:", clearCartError);
    }

    window.location.href = config.thankYouUrl;
  }

  async function buildAttachments(files, config, requestId) {
    if (!files.length) return [];

    const attachments = [];

    for (const file of files) {
      if (config.bucket) {
        try {
          attachments.push(await uploadAttachment(file, config, requestId));
          continue;
        } catch (error) {
          console.warn("Attachment upload failed, storing file name instead:", file.name, error);
        }
      }

      attachments.push(file.name);
    }

    return attachments;
  }

  async function saveQuoteRequest(payload, config) {
    const response = await fetch(
      `${config.supabaseUrl}/rest/v1/${encodeURIComponent(config.table)}`,
      {
        method: "POST",
        headers: buildSupabaseHeaders(config.supabaseAnonKey, {
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        }),
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const details = await response.text();
      throw new Error(parseSupabaseError(details) || "Could not save quote request");
    }
  }

  async function handleSubmit(event) {
    const form = event.currentTarget;
    const labels = getFormLabels(form);
    const config = {
      supabaseUrl: form.dataset.supabaseUrl?.replace(/\/$/, ""),
      supabaseAnonKey: form.dataset.supabaseAnonKey,
      table: form.dataset.supabaseTable || "quotes",
      bucket: form.dataset.supabaseBucket || "quote-attachments",
      thankYouUrl: form.dataset.thankYouUrl,
      quoteSource: form.dataset.quoteSource || "general",
      shopDomain: form.dataset.shopDomain || window.location.hostname,
    };

    event.preventDefault();
    clearError(form);

    const fileInput = form.querySelector("#QuoteForm-attachments");
    const files = Array.from(fileInput?.files || []);

    if (files.length > MAX_FILES) {
      showError(form, labels.errorMaxFiles);
      return;
    }

    if (files.some(function (file) { return file.size > MAX_FILE_SIZE; })) {
      showError(form, labels.errorFileSize);
      return;
    }

    setSubmitting(form, true);

    try {
      const cartItems = await fetchCartItems();

      if (isSupabaseConfigured(config)) {
        const requestId = crypto.randomUUID();
        const attachments = await buildAttachments(files, config, requestId);
        const payload = buildQuotePayload(form, cartItems, attachments, config);
        await saveQuoteRequest(payload, config);
        await finalizeQuoteSubmission(form, config, cartItems, attachments.length);
        return;
      }

      const payload = buildQuotePayload(form, cartItems, files.map(function (file) {
        return file.name;
      }), config);
      await submitViaShopifyContact(form, payload, files, config);
      await finalizeQuoteSubmission(form, config, cartItems, files.length);
    } catch (error) {
      console.error("Quote submission failed:", error);
      const message =
        error instanceof Error && error.message && !error.message.startsWith("{")
          ? error.message
          : labels.errorSubmit;
      showError(form, message);
      setSubmitting(form, false);
    }
  }

  function initAttachmentHint() {
    document.querySelectorAll(".quote-form__form").forEach(function (form) {
      const input = form.querySelector("#QuoteForm-attachments");
      const trigger = form.querySelector(".quote-form__file-trigger");
      const hint = form.querySelector("#QuoteForm-attachments-hint");
      const list = form.querySelector("#QuoteForm-attachments-list");
      const labels = getFormLabels(form);
      const defaultHint = labels.attachFilesDefault;

      if (!input || !trigger || !hint) return;

      trigger.addEventListener("click", function (event) {
        event.preventDefault();
        input.click();
      });

      input.addEventListener("change", function () {
        const files = Array.from(input.files || []);

        if (files.length > MAX_FILES) {
          hint.textContent = labels.errorMaxFiles;
          if (list) list.innerHTML = "";
          input.value = "";
          return;
        }

        if (files.some(function (file) { return file.size > MAX_FILE_SIZE; })) {
          hint.textContent = labels.errorFileSize;
          if (list) list.innerHTML = "";
          input.value = "";
          return;
        }

        hint.textContent = files.length
          ? formatFileSelectedLabel(labels, files.length)
          : defaultHint;

        if (list) {
          list.innerHTML = files
            .map(function (file) {
              return `<li>${file.name.replace(/</g, "&lt;")}</li>`;
            })
            .join("");
        }
      });
    });
  }

  function initDefaults() {
    document.querySelectorAll(".quote-form__form").forEach(function (form) {
      const countrySelect = form.querySelector("#QuoteForm-country");
      const defaultCountry = countrySelect?.dataset.defaultCountry;

      if (countrySelect && defaultCountry && !countrySelect.value) {
        countrySelect.value = defaultCountry;
      }
    });
  }

  function init() {
    const form = document.querySelector(".quote-form__form");
    const submitButton = form?.querySelector('[type="submit"] .btn__text');

    if (submitButton && !submitButton.dataset.defaultLabel) {
      submitButton.dataset.defaultLabel = submitButton.textContent.trim();
    }

    if (form) {
      form.addEventListener("submit", handleSubmit);
    }

    initDefaults();
    initAttachmentHint();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
